import { useEffect, useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import dayjs from 'dayjs';
import { EmployeeResponse } from 'generated/index';
import {
  FieldValues,
  SubmitHandler,
  UseFieldArrayAppend,
  useForm
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { InferType } from 'yup';
import { queries } from 'RestAPIs/rapini';

import { Box, Button } from '@mui/material';
import Grid from '@mui/material/Grid';

import {
  ControlledDatePicker,
  ControlledSearch,
  ControlledSelect,
  Drawer
} from 'components/shared';
import MultiStepForm from 'components/shared/MultiStepForm/MultiStepForm';

import { participantSchema } from '../add-edit-project-schema';
import { GridCompact12 } from 'components/shared/Grid/Grid.components';

type Form = InferType<typeof participantSchema>;

type Props = {
  open: boolean;
  handleClose: () => void;
  append: UseFieldArrayAppend<any, 'participants'>;
  update: (data: Form) => void;
  participant: Form;
  projectValidFrom: string;
  projectValidTo: string;
};

export default function AddEditParticipantDrawer({
  open,
  handleClose,
  append,
  update,
  participant,
  projectValidFrom,
  projectValidTo
}: Props) {
  const { t } = useTranslation('common');
  const methods = useForm({
    resolver: yupResolver(participantSchema)
  });

  const { watch } = methods;
  const employee = watch('employee');

  const [query, setQuery] = useState<string>('');
  const {
    data: employees,
    isLoading,
    refetch
  } = queries.useGetEmployees(undefined, undefined, query, {
    enabled: false
  });

  function onSubmit(data: Form) {
    if (participant) {
      const dataToSubmit = {
        ...data,
        entityId: data.employee.entityId,
        participantEntityId: data.employee.participantEntityId,
        name: data.employee.name
      };
      update(dataToSubmit);
    } else {
      const dataToSubmit = {
        ...data,
        employee: {
          entityId: null,
          participantEntityId: data.employee.entityId,
          name: data.employee.name
        },
        participantEntityId: data.employee.entityId,
        name: data.employee.name
      };
      append(dataToSubmit);
    }
    handleClose();
  }

  useEffect(() => {
    if (query.length > 0) {
      refetch();
    }
  }, [query]);

  useEffect(() => {
    setQuery('');
    if (participant) {
      methods.reset({
        employee: {
          label: participant.employee?.name || '',
          entityId: participant.employee?.entityId,
          participantEntityId: participant.employee?.participantEntityId,
          name: participant.employee?.name
        },
        role: participant.role.toUpperCase(),
        validFrom: dayjs(participant.validFrom),
        validTo: dayjs(participant.validTo)
      });
    } else {
      methods.reset({
        employee: null,
        role: '',
        validFrom: dayjs(projectValidFrom),
        validTo: dayjs(projectValidTo)
      });
    }
  }, [participant, projectValidFrom, projectValidTo]);

  return (
    <Drawer
      title={
        participant
          ? t('activityPlanning.editEmployee')
          : t('activityPlanning.addEmployee')
      }
      anchor="right"
      open={open}
      onClose={handleClose}
    >
      <MultiStepForm
        methods={methods}
        onSubmit={methods.handleSubmit(onSubmit as SubmitHandler<FieldValues>)}
        style={{
          minHeight: 'calc(100vh - 160px)'
        }}
      >
        <Grid py={6} container spacing={2} sx={{ maxWidth: '428px' }}>
          <GridCompact12>
            <ControlledSearch
              name="employee"
              control={methods.control}
              idProperty="participantEntityId"
              labelProperty="label"
              label={t('employee')}
              noOptionsText={t('searchEmployee')}
              loading={isLoading}
              fullWidth
              debounceInMS={500}
              options={(employees || []).map((employee: EmployeeResponse) => ({
                label: employee?.nameInformal || '',
                entityId: employee?.entityId,
                name: employee?.nameInformal
              }))}
              value={
                employee && {
                  label: employee.label || '',
                  participantEntityId: employee.participantEntityId,
                  name: employee.name
                }
              }
              onQueryChange={setQuery}
            />
          </GridCompact12>
          <GridCompact12>
            <ControlledSelect
              name="role"
              control={methods.control}
              options={[
                { label: t('roles.MANAGER'), value: 'MANAGER' },
                { label: t('roles.TEAM_LEAD'), value: 'TEAM_LEAD' },
                { label: t('roles.EMPLOYEE'), value: 'EMPLOYEE' }
              ]}
              label={t('role')}
              variant="outlined"
            />
          </GridCompact12>
          <GridCompact12>
            <Box display="flex" gap={1}>
              <ControlledDatePicker
                name="validFrom"
                control={methods.control}
                label={t('validFrom')}
                variant="outlined"
              />
              <ControlledDatePicker
                name="validTo"
                control={methods.control}
                label={t('validTo')}
                variant="outlined"
              />
            </Box>
          </GridCompact12>
        </Grid>
        <MultiStepForm.Actions>
          <Button
            type="submit"
            variant="contained"
            disabled={!methods.formState.isDirty}
            color="primary"
          >
            {participant ? t('edit') : t('add')}
          </Button>
        </MultiStepForm.Actions>
      </MultiStepForm>
    </Drawer>
  );
}
